import { ApiResponse } from "../network/ApiResponse";
import httpClient from "../network/httpClient";
import { Result } from "../Result";
import {
  CreateUserRequest,
  CreateUserResponse,
  FetchAIRequest,
  FetchAIResponse,
  GetUserListResponse,
} from "./user.api.interface";

// ReportInterval enum for analytics/report queries
export enum ReportInterval {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  CUSTOM = 'CUSTOM',
}

class UserApi {
  public async createUser(requestData: CreateUserRequest): Promise<ApiResponse<CreateUserResponse>> {
    const response = await httpClient.post<CreateUserResponse>("contact/create", requestData);
    return response;
  }

  // Method for fetching the list of users
  public async getUserList(page: number = 1, status: string): Promise<ApiResponse<GetUserListResponse>> {
    const url = status === "all" ? `users/all?page=${page}` : `users/all?page=${page}&status=${status}`; // Remove status if it's "all"
    const response = await httpClient.get<GetUserListResponse>(url);
    return response;
  }

  public async getUserConfig(page: number): Promise<ApiResponse<GetUserListResponse>> {
    const response = await httpClient.get<GetUserListResponse>(`app/config/get/${page}`);
    return response;
  }
  public async getDefaultConfig(page: number): Promise<ApiResponse<GetUserListResponse>> {
    const response = await httpClient.get<GetUserListResponse>(`app/config/get-default`);
    return response;
  }
  public async setUserConfig(requestData: any): Promise<ApiResponse<CreateUserResponse>> {
    const response = await httpClient.post<CreateUserResponse>("app/config/set", requestData);
    return response;
  }
  public async setDefaultConfig(requestData: any): Promise<ApiResponse<CreateUserResponse>> {
    const response = await httpClient.post<CreateUserResponse>("app/config/set-default", requestData);
    return response;
  }
  public async getShortcode(): Promise<ApiResponse<any>> {
    const response = await httpClient.get<any>(`shortcode/all`);
    return response;
  }
  public async setShortcode(requestData: any): Promise<ApiResponse<CreateUserResponse>> {
    const response = await httpClient.post<CreateUserResponse>("shortcode/add", requestData);
    return response;
  }
  public async updateShortcode(requestData: any): Promise<ApiResponse<CreateUserResponse>> {
    const response = await httpClient.put<CreateUserResponse>("shortcode/update", requestData);
    return response;
  }
  public async deleteShortcode(id: any): Promise<ApiResponse<CreateUserResponse>> {
    const response = await httpClient.delete<CreateUserResponse>(`shortcode/delete/${id}`);
    return response;
  }

  public async applyShortcode(requestData: { userId: any; shortCodeId: any }): Promise<ApiResponse<CreateUserResponse>> {
    const response = await httpClient.post<CreateUserResponse>("users/assign-shortcode", requestData);
    return response;
  }

  public async unassignShortcode(shortCodeId: number): Promise<ApiResponse<any>> {
    try {
      const response = await httpClient.post<any>(`users/revoke-shortcode/${shortCodeId}`);
      return response;
    } catch (error) {
      console.error("Error unassigning shortcode:", error);
      throw error;
    }
  }

  public async fetchAI(requestData: FetchAIRequest): Promise<ApiResponse<FetchAIResponse>> {
    const response = await httpClient.post<FetchAIResponse>("/get-sample-text", requestData);
    return response;
  }

  public async getRcsBots(): Promise<ApiResponse<any>> {
    const response = await httpClient.get<any>(`rcs-bots/all`);
    return response;
  }

  public async setRcsBot(requestData: any): Promise<ApiResponse<CreateUserResponse>> {
    const response = await httpClient.post<CreateUserResponse>("rcs-bots/add", requestData);
    return response;
  }

  public async updateRcsBot(requestData: any): Promise<ApiResponse<CreateUserResponse>> {
    const response = await httpClient.put<CreateUserResponse>("rcs-bots/update", requestData);
    return response;
  }

  public async deleteRcsBot(id: any): Promise<ApiResponse<CreateUserResponse>> {
    const response = await httpClient.delete<CreateUserResponse>(`rcs-bots/delete/${id}`);
    return response;
  }

  async deleteUser(userIds: string[]): Promise<Result<void>> {
    try {
      const response = await httpClient.post<void>("users/delete", { ids: userIds });
      console.log('response', response);
      return { success: true, value: undefined };
    } catch (error) {
      console.error("UserApi -> deleteUser failed:", error);
      return { success: false, message: "Network error" };
    }
  }

  public async assignBotToUser(payload: { botId: number; userId: string }): Promise<ApiResponse<any>> {
    try {
      const response = await httpClient.post<any>("users/assign-rcs-bot", payload);
      return response;
    } catch (error) {
      console.error("Error assigning bot to user:", error);
      throw error;
    }
  }

  public async unassignBotFromUser(botId: number): Promise<ApiResponse<any>> {
    try {
      const response = await httpClient.post<any>(`users/revoke-rcs-bot/${botId}`);
      return response;
    } catch (error) {
      console.error("Error unassigning bot:", error);
      throw error;
    }
  }

  public async getSubscriptions(): Promise<ApiResponse<any>> {
    const response = await httpClient.get<any>("subscription/all");
    return response;
  }

  public async updateSubscription(requestData: { userId: number; endDate: number }): Promise<ApiResponse<any>> {
    const response = await httpClient.post<any>("subscription/update", requestData);
    return response;
  }

  /**
   * Get dashboard data with optional report interval and time range
   * @param params { type?: ReportInterval, startTime?: number, endTime?: number }
   */
  public async getDashboardData(params?: { type?: ReportInterval; startTime?: number; endTime?: number }): Promise<ApiResponse<any>> {
    let url = "report/users";
    if (params) {
      const query = [];
      if (params.type) query.push(`type=${params.type}`);
      if (params.startTime) query.push(`startTime=${params.startTime}`);
      if (params.endTime) query.push(`endTime=${params.endTime}`);
      if (query.length) url += `?${query.join("&")}`;
    }
    const response = await httpClient.get<any>(url);
    return response;
  }

  /**
   * Get analytics data with optional report interval and time range
   * @param params { type?: ReportInterval, startTime?: number, endTime?: number }
   */
  public async getAnalyticsData(params?: { type?: ReportInterval; startTime?: number; endTime?: number }): Promise<ApiResponse<any>> {
    let url = "report/analytics";
    if (params) {
      const query = [];
      // Only add type if it's not CUSTOM
      if (params.type && params.type !== ReportInterval.CUSTOM) query.push(`type=${params.type}`);
      
        if (params.startTime) query.push(`startTime=${params.startTime}`);
        if (params.endTime) query.push(`endTime=${params.endTime}`);
  
      if (query.length) url += `?${query.join("&")}`;
    }
    const response = await httpClient.get<any>(url);
    return response;
  }
  public async getNoShortcodesUser(): Promise<ApiResponse<any>> {
    try {
      const response = await httpClient.get<any>("users/no-shortcode");
      return response;
    } catch (error) {
      console.error("Error fetching users with no shortcodes:", error);
      throw error;
    }
  }
  
  public async getNoBotsUser(): Promise<ApiResponse<any>> {
    try {
      const response = await httpClient.get<any>("users/no-rcs-bots");
      return response;
    } catch (error) {
      console.error("Error fetching users with no bots:", error);
      throw error;
    }
  }
  /**
   * Fetch all feedback entries with optional filters
   * @param params { pageNumber?: number, type?: ReportInterval, startTime?: number, endTime?: number }
   */
  public async getFeedbackList(params?: { pageNumber?: number; type?: ReportInterval; startTime?: number; endTime?: number }): Promise<ApiResponse<any>> {
    let url = "feedback/all";
    if (params) {
      const query: string[] = [];
      if (params.pageNumber !== undefined) query.push(`pageNumber=${params.pageNumber}`);
      if (params.type) query.push(`type=${params.type}`);
      if (params.startTime) query.push(`startTime=${params.startTime}`);
      if (params.endTime) query.push(`endTime=${params.endTime}`);
      if (query.length) url += `?${query.join("&&")}`;
    }
    const response = await httpClient.get<any>(url);
    return response;
  }
}

const usersApi = new UserApi();
export default usersApi;
